import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import * as adminApi from '@/api/admin';
import type {
  AdminPriceSummaryQuery,
  AdminProductPriceListResult,
  AdminProductPriceQuery,
  AdminProductPriceRow,
  AdminProductQuery,
  CalibrateTemplatePayload,
  ChannelReleaseStates,
  CreateMockupTemplatePayload,
  MockupLayerType,
  PriceRowParams,
  ReleaseChannelKey,
  SetProductCarryOverPayload,
  UpdateMockupTemplatePayload,
  UpdatePrintAreaPayload,
} from '@/api/admin';

// ═══════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════

/** Marker that tells a price-view list key apart from a classic list key. */
const PRICE_VIEW = { view: 'prices' } as const;

export const adminProductKeys = {
  all: ['admin', 'products'] as const,
  lists: () => [...adminProductKeys.all, 'list'] as const,
  list: (query?: AdminProductQuery) =>
    [...adminProductKeys.lists(), query] as const,
  /** Price table page; lives under `lists()` so list invalidation covers it. */
  priceList: (query: AdminProductPriceQuery = {}) =>
    [...adminProductKeys.lists(), { ...PRICE_VIEW, ...query }] as const,
  priceSummaries: () => [...adminProductKeys.all, 'price-summary'] as const,
  priceSummary: (query: AdminPriceSummaryQuery = {}) =>
    [...adminProductKeys.priceSummaries(), query] as const,
  sizePrices: (productId: string) =>
    [...adminProductKeys.all, 'size-prices', productId] as const,
  details: () => [...adminProductKeys.all, 'detail'] as const,
  detail: (id: string) => [...adminProductKeys.details(), id] as const,
  carryOverImpact: (id: string) =>
    [...adminProductKeys.all, 'carry-over-impact', id] as const,
};

// ═══════════════════════════════════════════════════════════════════
// PRICE ROW CACHE
// ═══════════════════════════════════════════════════════════════════

function priceListQueryOf(
  queryKey: readonly unknown[]
): AdminProductPriceQuery | null {
  const query = queryKey[adminProductKeys.lists().length];
  return typeof query === 'object' &&
    query !== null &&
    (query as { view?: unknown }).view === PRICE_VIEW.view
    ? (query as AdminProductPriceQuery)
    : null;
}

function isPriceListKey(queryKey: readonly unknown[]): boolean {
  return priceListQueryOf(queryKey) !== null;
}

/**
 * True when the page was fetched with the same platform parameters as the
 * row, i.e. the row's Seller cost and decoration columns fit the page.
 */
function samePriceRowParams(
  queryKey: readonly unknown[],
  params: PriceRowParams
): boolean {
  const query = priceListQueryOf(queryKey);
  return (
    query !== null &&
    query.platform === params.platform &&
    query.ekPlatform === params.ekPlatform
  );
}

/**
 * Replaces one row in every cached price-view page fetched with the same
 * platform parameters. Pages without the row and all other rows keep their
 * identity, so only the changed row re-renders. Pages of other platforms
 * hold the product with values that were computed for them and are marked
 * stale instead, so they refetch when the admin switches back.
 */
export function replacePriceRowInCache(
  queryClient: QueryClient,
  row: AdminProductPriceRow,
  params: PriceRowParams
): void {
  queryClient.setQueriesData<AdminProductPriceListResult>(
    {
      queryKey: adminProductKeys.lists(),
      predicate: (query) => samePriceRowParams(query.queryKey, params),
    },
    (page) => {
      if (!page) return page;
      const index = page.data.findIndex((cached) => cached.id === row.id);
      if (index === -1) return page;
      const data = page.data.slice();
      data[index] = row;
      return { ...page, data };
    }
  );
  void queryClient.invalidateQueries({
    queryKey: adminProductKeys.lists(),
    predicate: (query) =>
      isPriceListKey(query.queryKey) &&
      !samePriceRowParams(query.queryKey, params),
    refetchType: 'none',
  });
}

/**
 * Fetches the row with the table's current parameters and swaps it into
 * the cache; the summary counts are invalidated because every price write
 * can move them. Falls back to invalidating the lists when the row fetch
 * fails, so the table still catches up with the saved value.
 */
export async function refreshPriceRow(
  queryClient: QueryClient,
  productId: string,
  params: PriceRowParams
): Promise<void> {
  try {
    const row = await adminApi.getAdminProductPriceRow(productId, params);
    replacePriceRowInCache(queryClient, row, params);
  } catch {
    await queryClient.invalidateQueries({
      queryKey: adminProductKeys.lists(),
    });
  }
  void queryClient.invalidateQueries({
    queryKey: adminProductKeys.priceSummaries(),
  });
}

/** Options of mutations that may run on the price table. */
export interface PriceRowRefreshOptions {
  /**
   * When set, the mutation refreshes the product's price row with these
   * parameters instead of invalidating the product lists.
   */
  priceRowParams?: PriceRowParams;
}

// ═══════════════════════════════════════════════════════════════════
// QUERIES
// ═══════════════════════════════════════════════════════════════════

/**
 * Hook to fetch products for admin configuration (paginated)
 */
export function useAdminProducts(query?: AdminProductQuery) {
  return useQuery({
    queryKey: adminProductKeys.list(query),
    queryFn: () => adminApi.getAdminProducts(query),
    placeholderData: (previous) => previous,
  });
}

/**
 * Hook to activate/deactivate a product. On the price table (with
 * `priceRowParams`) only the product's row is refreshed; otherwise the
 * product lists are invalidated as before.
 */
export function useSetProductActive(options: PriceRowRefreshOptions = {}) {
  const queryClient = useQueryClient();
  const { priceRowParams } = options;

  return useMutation({
    mutationFn: ({
      productId,
      isActive,
    }: {
      productId: string;
      isActive: boolean;
    }) => adminApi.setProductActive(productId, isActive),
    onSuccess: async (_data, { productId }) => {
      if (priceRowParams) {
        await refreshPriceRow(queryClient, productId, priceRowParams);
      } else {
        queryClient.invalidateQueries({ queryKey: adminProductKeys.lists() });
      }
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.detail(productId),
      });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Marks or unmarks a product as end of life; refreshes the price row when
 * called from the price table, the product lists otherwise.
 */
export function useSetProductEndOfLife(options: PriceRowRefreshOptions = {}) {
  const queryClient = useQueryClient();
  const { priceRowParams } = options;

  return useMutation({
    mutationFn: ({
      productId,
      endOfLife,
    }: {
      productId: string;
      endOfLife: boolean;
    }) => adminApi.setProductEndOfLife(productId, endOfLife),
    onSuccess: async (_data, { productId }) => {
      if (priceRowParams) {
        await refreshPriceRow(queryClient, productId, priceRowParams);
      } else {
        queryClient.invalidateQueries({ queryKey: adminProductKeys.lists() });
      }
    },
  });
}

/**
 * Writes the release states the API answered into every cached price page
 * that holds the product. Release states do not depend on the platform
 * parameters, so every page may take them.
 */
function patchChannelsInCache(
  queryClient: QueryClient,
  productId: string,
  channels: ChannelReleaseStates
): void {
  queryClient.setQueriesData<AdminProductPriceListResult>(
    {
      queryKey: adminProductKeys.lists(),
      predicate: (query) => isPriceListKey(query.queryKey),
    },
    (page) => {
      if (!page) return page;
      if (!page.data.some((cached) => cached.id === productId)) return page;
      const data = page.data.map((cached) =>
        cached.id === productId ? { ...cached, channels } : cached
      );
      return { ...page, data };
    }
  );
}

/**
 * Releases or withdraws a product on one admin channel. A success writes
 * the answered states into the cached rows, then refreshes the row and
 * the summary (whose channel counts move); the storefront caches follow.
 * The optimistic state and its rollback live in `ChannelReleaseCell`.
 */
export function useSetProductChannelRelease(priceRowParams: PriceRowParams) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      channelKey,
      released,
    }: {
      productId: string;
      channelKey: ReleaseChannelKey;
      released: boolean;
    }) => adminApi.setProductChannelRelease(productId, channelKey, released),
    onSuccess: async (result, { productId }) => {
      patchChannelsInCache(queryClient, productId, result.channels);
      await refreshPriceRow(queryClient, productId, priceRowParams);
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Hook to fetch a single product with positions, print areas and templates
 */
export function useAdminProduct(id: string | undefined) {
  return useQuery({
    queryKey: adminProductKeys.detail(id!),
    queryFn: () => adminApi.getAdminProduct(id!),
    enabled: !!id,
  });
}

// ═══════════════════════════════════════════════════════════════════
// MUTATIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * How many stored positions the product's carry-over settings reach, in
 * both directions. The counts do not depend on the values, so the admin
 * sees them before saving.
 */
export function useCarryOverImpact(productId: string | undefined) {
  return useQuery({
    queryKey: adminProductKeys.carryOverImpact(productId!),
    queryFn: () => adminApi.getCarryOverImpact(productId!),
    enabled: !!productId,
  });
}

/**
 * Saves the product-level carry-over settings. The detail, the lists (the
 * price table shows class and percent), the creator's product cache and the
 * impact (its area-exception list) are refreshed.
 */
export function useSetProductCarryOver(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SetProductCarryOverPayload) =>
      adminApi.setProductCarryOver(productId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.detail(productId),
      });
      queryClient.invalidateQueries({ queryKey: adminProductKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.carryOverImpact(productId),
      });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Hook to save the print area (design area) of a position
 */
export function useUpdatePrintArea(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      positionId,
      payload,
    }: {
      positionId: string;
      payload: UpdatePrintAreaPayload;
    }) => adminApi.updatePrintArea(positionId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.detail(productId),
      });
      // An area exception changes the impact's exception list.
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.carryOverImpact(productId),
      });
      // Creator views cache product data under the 'products' key
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Hook to calibrate one mockup template (default or not) from the frame
 * drawn on it. Waits for the product detail to reload, so the caller reads
 * the new status as soon as the save resolves.
 */
export function useCalibrateTemplate(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      payload,
    }: {
      templateId: string;
      payload: CalibrateTemplatePayload;
    }) => adminApi.calibrateTemplate(productId, templateId, payload),
    onSuccess: async () => {
      // Creator views cache product data under the 'products' key; a newly
      // calibrated template can make a product selectable there.
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      await queryClient.invalidateQueries({
        queryKey: adminProductKeys.detail(productId),
      });
    },
  });
}

/**
 * Hook to set or clear the Direct Sale surcharge of a position.
 * null clears the price and removes the position from the Direct Sale flow.
 */
export function useSetPositionSurcharge(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      positionId,
      printSurcharge,
    }: {
      positionId: string;
      printSurcharge: number | null;
    }) => adminApi.setPositionSurcharge(positionId, printSurcharge),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.detail(productId),
      });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Hook to create a mockup template
 */
export function useCreateMockupTemplate(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateMockupTemplatePayload) =>
      adminApi.createMockupTemplate(productId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.detail(productId),
      });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Hook to update mockup template settings
 */
export function useUpdateMockupTemplate(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      payload,
    }: {
      templateId: string;
      payload: UpdateMockupTemplatePayload;
    }) => adminApi.updateMockupTemplate(templateId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.detail(productId),
      });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Hook to delete a mockup template
 */
export function useDeleteMockupTemplate(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: string) =>
      adminApi.deleteMockupTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.detail(productId),
      });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Hook to upload or replace a mockup layer image
 */
export function useUploadMockupLayer(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      layerType,
      file,
    }: {
      templateId: string;
      layerType: MockupLayerType;
      file: File;
    }) => adminApi.uploadMockupLayer(templateId, layerType, file),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.detail(productId),
      });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Hook to remove an optional mockup layer image
 */
export function useRemoveMockupLayer(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      layerType,
    }: {
      templateId: string;
      layerType: MockupLayerType;
    }) => adminApi.removeMockupLayer(templateId, layerType),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.detail(productId),
      });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}
